import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
 breadcrumbListSchema,
 collectionPageSchema,
 faqPageSchema,
 jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import {
 SEO_CITIES,
 SEO_SPECIALTIES,
 citySlug,
 specialtySlug,
 insuranceFromSlug,
} from "@/lib/seo-slugs";
import { MapPin, ShieldCheck, Search } from "lucide-react";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/insurance/$slug")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const insurance = insuranceFromSlug(params.slug);
 if (!insurance) throw notFound();
 return { insurance };
 },
 head: ({ params, loaderData }) => {
 const ins = loaderData?.insurance;
 const name = ins?.name ?? "Insurance";
 const short = ins?.short ?? name;
 const title = `Doctors That Accept ${name}, Find Providers Near You | Heallexa`;
 const description = `Find doctors, dentists, therapists, and specialists that accept ${name} (${short}). Browse by city and specialty on Heallexa, free, no signup required.`;
 const url = `${SITE}/insurance/${params.slug}`;
 const decision = decideIndexability({
 providerCount: 1,
 hasFaq: true,
 hasNearbyCities: true,
 hasRelatedProviders: true,
 internalLinkCount: 40,
 });
 return {
 meta: [
 { title },
 { name: "description", content: description },
 robotsMeta(decision.robots),
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 jsonLdScript(collectionPageSchema({ name: title, description, url })),
 jsonLdScript(
 breadcrumbListSchema([
 { name: "Home", url: "/" },
 { name: "Insurance", url: "/insurance" },
 { name, url: `/insurance/${params.slug}` },
 ]),
 ),
 jsonLdScript(
 faqPageSchema([
 { q: `How do I find doctors that accept ${short}?`, a: `Pick a specialty or city below to see ${short} providers on Heallexa. Always confirm in-network status with the office.` },
 { q: `Is ${short} accepted by most providers?`, a: `${name} is one of the larger U.S. carriers, but networks vary by plan tier and region.` },
 { q: `Does Heallexa charge to book ${short} providers?`, a: `No. Heallexa is free for patients to search and book.` },
 ]),
 ),
 ],
 };
 },
 component: InsurancePage,
 notFoundComponent: () => (
 <div className="min-h-screen flex items-center justify-center">
 <div className="text-center">
 <h1 className="font-display text-2xl font-semibold mb-2">Insurance not found</h1>
 <Link to="/insurance" className="text-primary underline">Browse all carriers</Link>
 </div>
 </div>
 ),
 errorComponent: ({ error }) => (
 <div className="min-h-screen flex items-center justify-center">
 <p className="text-muted-foreground">{error.message}</p>
 </div>
 ),
});

function InsurancePage() {
 const { insurance } = Route.useLoaderData();
 const topSpecialties = SEO_SPECIALTIES.slice(0, 16);
 const topCities = SEO_CITIES;

 const specialtyLinks = topSpecialties.map((s) => ({
 label: `${s} accepting ${insurance.short}`,
 href: `/specialties/${specialtySlug(s)}`,
 }));

 const cityLinks = topCities.slice(0, 24).map((c) => ({
 label: `${insurance.short} providers in ${c}`,
 href: `/insurance/${insurance.id}/${citySlug(c)}`,
 }));

 const popularCombos = topSpecialties.slice(0, 8).flatMap((sp) =>
 topCities.slice(0, 3).map((ct) => ({
 label: `${sp} in ${ct.split(",")[0]} accepting ${insurance.short}`,
 href: `/specialty/${specialtySlug(sp)}/${citySlug(ct)}`,
 })),
 );

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <Breadcrumbs
 className="mb-6"
 items={[
 { name: "Home", href: "/" },
 { name: "Insurance", href: "/insurance" },
 { name: insurance.name },
 ]}
 />

 <Reveal from="up">
 <header className="mb-12">
 <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4 inline-flex items-center gap-2">
 <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
 Insurance directory
 </p>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
 Doctors that accept {insurance.name}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">
 Browse healthcare providers across the U.S. that may accept {insurance.name} ({insurance.short}). Pick a specialty or city below to see matching providers on Heallexa.
 </p>
 <div className="mt-6 flex flex-wrap gap-3">
 <Link
 to="/search"
 search={{ insurance: insurance.short }}
 className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
 >
 <Search className="h-4 w-4" />
 Search {insurance.short} providers
 </Link>
 <Link
 to="/browse"
 className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors rounded-md"
 >
 Browse all providers
 </Link>
 </div>
 </header>
 </Reveal>

 <Reveal from="up" delay={60}>
 <SeoLinkList heading={`${insurance.short} providers by specialty`} links={specialtyLinks} columns={4} className="mb-10" />
 </Reveal>
 <Reveal from="up" delay={60}>
 <SeoLinkList heading={`${insurance.short} providers by city`} links={cityLinks} columns={3} icon={MapPin} className="mb-10" />
 </Reveal>
 <Reveal from="up" delay={60}>
 <SeoLinkList heading="Popular searches" links={popularCombos} columns={3} className="mb-10" />
 </Reveal>

 <Reveal from="up" delay={60}>
 <section className="card-surface rounded-md p-6 sm:p-8 mb-12">
 <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground mb-3">About {insurance.name}</h2>
 <p className="text-[14px] leading-relaxed text-muted-foreground">
 {insurance.name} is one of many health insurance plans accepted by providers listed on Heallexa. Coverage, copays, and in-network status vary by plan and provider, always confirm directly with the provider's office before booking.
 </p>
 <p className="text-[14px] leading-relaxed text-muted-foreground mt-3">
 Heallexa is a free directory of healthcare providers across the United States. We don't charge patients to search and we don't take a cut of bookings.
 </p>
 </section>
 </Reveal>

 <SeoFaq
 items={[
 { q: `How do I find doctors that accept ${insurance.short}?`, a: `Pick a specialty or city above. Then verify in-network status with the provider's office before your appointment.` },
 { q: `What if my plan tier isn't shown?`, a: `Provider networks differ by plan tier. Confirm directly with the provider that they accept your specific ${insurance.short} plan.` },
 { q: `Can I book directly on Heallexa?`, a: `Yes, many providers support online booking. Look for the "Book online" button on the provider's profile.` },
 { q: `Is Heallexa free?`, a: `Yes, completely free for patients.` },
 ]}
 />
 </main>
 <FooterSection />
 </div>
 );
}
